import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BadgeComponent,
  ButtonDirective,
  CardBodyComponent,
  CardComponent,
  CardHeaderComponent,
  ContainerComponent
} from '@coreui/angular-pro';
import { RouterLink } from '@angular/router';
import { AuthService } from 'auth';
import { TranslatePipe, TranslateService } from 'i18n';
import { Logger, isAdmin } from 'shared';
import { AppointmentOccurrence, AppointmentService } from '../../../services/appointment.service';

/** One calendar day of the member list, in the reader's own time zone. */
interface AppointmentDay {
  /** ISO date in the reader's zone — the grouping key, not a display value. */
  key: string;
  heading: string;
  isToday: boolean;
  isTomorrow: boolean;
  occurrences: AppointmentOccurrence[];
}

/**
 * Member-facing appointment list ("Termine"): everything happening in the next
 * four weeks, starting today.
 *
 * Two time zones are on screen at once, deliberately:
 *
 *  - the reader's own, because that is the one they have to act on, and
 *  - the appointment's declared zone, because that is the number the organiser
 *    announces and the one written on the invitation.
 *
 * Neither is ever shown as a bare clock time — a call that reads "20:00" with no
 * zone next to it is the exact failure this page exists to avoid.
 */
@Component({
  selector: 'app-appointments-page',
  standalone: true,
  imports: [
    CommonModule,
    ContainerComponent,
    CardComponent,
    CardHeaderComponent,
    CardBodyComponent,
    BadgeComponent,
    ButtonDirective,
    RouterLink,
    TranslatePipe
  ],
  templateUrl: './appointments-page.component.html',
  styleUrls: ['./appointments-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppointmentsPageComponent {
  private logger = new Logger('AppointmentsPageComponent');
  private service = inject(AppointmentService);
  private translate = inject(TranslateService);
  private auth = inject(AuthService);
  private destroyRef = inject(DestroyRef);

  /** The window the backend defaults to; stated here only so the heading can name it. */
  readonly weeks = 4;

  /** The reader's own zone, straight from the browser. */
  readonly userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  occurrences = signal<AppointmentOccurrence[]>([]);
  loading = signal(true);
  error = signal<string | null>(null);

  /**
   * Admins manage the schedule from here, not only from the sidebar's admin
   * block: this page is where someone goes when they think about appointments,
   * and an empty list with no way to add anything is a dead end. The link is the
   * only admin affordance on the page — the editing itself lives on its own
   * screen, so a member's view of this page is unchanged.
   */
  canManage = signal(false);

  days = computed<AppointmentDay[]>(() => {
    const groups = new Map<string, AppointmentDay>();
    const today = this.isoDay(new Date());
    const tomorrow = this.isoDay(new Date(Date.now() + 86_400_000));

    for (const occurrence of this.occurrences()) {
      // Grouped by the day it falls on for *the reader*, not by the appointment's
      // own date: a 23:00 Vienna call is "tomorrow" to a member in Tokyo, and
      // filing it under the Vienna date would put it in the wrong place in a list
      // that is otherwise strictly chronological for them.
      const start = new Date(occurrence.starts_at);
      const key = this.isoDay(start);

      let group = groups.get(key);
      if (!group) {
        group = {
          key,
          heading: this.formatDayHeading(start),
          isToday: key === today,
          isTomorrow: key === tomorrow,
          occurrences: []
        };
        groups.set(key, group);
      }
      group.occurrences.push(occurrence);
    }

    return [...groups.values()];
  });

  constructor() {
    this.load();

    // The language selects the translation of title and details (resolved on
    // the backend), so a language switch is a reload, not a re-render.
    this.translate.langChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load());

    // Subscribed rather than read once: on a hard reload the guard lets the page
    // render before the user has been resolved, and a one-shot read would leave
    // an admin looking at a page with no way in.
    this.auth.getUser$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(user => this.canManage.set(isAdmin(user)));
  }

  /** Language-prefixed, like every other route in these apps. */
  get adminLink(): string {
    return `/${this.translate.getActiveLang() || 'en'}/admin/appointments`;
  }

  private load(): void {
    this.loading.set(true);
    this.error.set(null);

    this.service.getUpcoming(this.translate.getActiveLang(), this.weeks)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.occurrences.set(response.appointments);
          this.loading.set(false);
        },
        error: err => {
          this.logger.error('Failed to load appointments:', err);
          this.error.set(this.translate.translate('appointments.load_failed'));
          this.occurrences.set([]);
          this.loading.set(false);
        }
      });
  }

  /** Start time in the reader's zone, e.g. "20:00". */
  localTime(occurrence: AppointmentOccurrence): string {
    return this.timeFormatter.format(new Date(occurrence.starts_at));
  }

  /** End time in the reader's zone, or null when no duration was announced. */
  localEndTime(occurrence: AppointmentOccurrence): string | null {
    if (!occurrence.ends_at) {
      return null;
    }
    return this.timeFormatter.format(new Date(occurrence.ends_at));
  }

  /**
   * Short name of the reader's zone at that moment, e.g. "GMT+2" or "CEST".
   * Resolved per occurrence rather than once, so a date after the DST switch is
   * not labelled with today's offset.
   */
  localZoneLabel(occurrence: AppointmentOccurrence): string {
    const parts = this.zoneFormatter.formatToParts(new Date(occurrence.starts_at));
    return parts.find(part => part.type === 'timeZoneName')?.value ?? this.userTimeZone;
  }

  /** True when the reader is in the same zone the appointment was entered in. */
  sameZoneAsReader(occurrence: AppointmentOccurrence): boolean {
    return occurrence.time_zone === this.userTimeZone;
  }

  private get locale(): string {
    return this.translate.getActiveLang() || 'en';
  }

  private get timeFormatter(): Intl.DateTimeFormat {
    return new Intl.DateTimeFormat(this.locale, {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: this.userTimeZone
    });
  }

  private get zoneFormatter(): Intl.DateTimeFormat {
    return new Intl.DateTimeFormat(this.locale, {
      timeZoneName: 'short',
      timeZone: this.userTimeZone
    });
  }

  private formatDayHeading(date: Date): string {
    return new Intl.DateTimeFormat(this.locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: this.userTimeZone
    }).format(date);
  }

  /**
   * The calendar date of `date` in the reader's zone, as YYYY-MM-DD.
   *
   * Built from formatted parts rather than from `toISOString()`, which would
   * answer in UTC and mis-file every late-evening appointment east of Greenwich.
   */
  private isoDay(date: Date): string {
    const parts = new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: this.userTimeZone
    }).formatToParts(date);

    const get = (type: string) => parts.find(part => part.type === type)?.value ?? '';
    return `${get('year')}-${get('month')}-${get('day')}`;
  }
}
